import {IndexedDBCache} from "./indexed_db_cache";
import {Tile} from "./tile";
import {fetchAndCacheBlob, fetchAndCacheImage} from "./fetch_cached_image";
import {validateCoordinates} from "./validation_utils";
import {MeasurementPrefUtility} from "../helpers/MeasurementPref";

async function getElevationFromBlob(
  blob: Blob,
  x: number,
  y: number
): Promise<number> {
  // Create an ImageBitmap from the blob
  const imageBitmap = await createImageBitmap(blob);

  // Use OffscreenCanvas if available, otherwise fallback to regular canvas
  const canvas =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(imageBitmap.width, imageBitmap.height)
      : document.createElement('canvas');

  canvas.width = imageBitmap.width;
  canvas.height = imageBitmap.height;

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get 2D context');
  }

  // Draw the ImageBitmap onto the canvas
  ctx.drawImage(imageBitmap, 0, 0);

  // Get pixel data at the specified coordinates
  const pixelData = ctx.getImageData(x, y, 1, 1).data;

  const R = pixelData[0];
  const G = pixelData[1];
  const B = pixelData[2];

  const elevation = -10000 + ((R * 256 * 256 + G * 256 + B) * 0.1);
  return elevation;
}


function getElevationAtPixel(
  image: HTMLImageElement,
  x: number,
  y: number
): number {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get 2D context');
  }

  ctx.drawImage(image, 0, 0);
  const pixelData = ctx.getImageData(x, y, 1, 1).data;

  const R = pixelData[0];
  const G = pixelData[1];
  const B = pixelData[2];

  const elevation = -10000 + ((R * 256 * 256 + G * 256 + B) * 0.1);
  return elevation;
}

function getPixelCoordinates(
  coord: { lat: number; lng: number },
  tile: Tile,
  tileSize: number = 512
): { x: number; y: number } {
  const n = Math.pow(2, tile.z);
  const lonDeg = coord.lng;
  const latRad = (coord.lat * Math.PI) / 180;

  const x = ((lonDeg + 180) / 360) * n * tileSize;
  const y =
    ((1 -
        Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) /
      2) *
    n *
    tileSize;

  const pixelX = Math.floor(x - tile.x * tileSize);
  const pixelY = Math.floor(y - tile.y * tileSize);

  return { x: pixelX, y: pixelY };
}

export async function getElevation(
  coord: { lat: number; lng: number },
  zoom: number = 10,
  cache: IndexedDBCache
): Promise<string> {
  // Validate coordinates
  let accessToken = "pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2xydDBwMTU4MDFmdDJwbzd5NjFxMmlyciJ9.Q0bXbghB7N2Ji93oLrAJng"
  validateCoordinates(coord.lat, coord.lng);

  if (zoom < 10) {
    return ''
  }
  if (zoom > 10) {
    zoom = 10
  }
  const tile = Tile.fromCoordinates(coord.lat, coord.lng, zoom);
  const tileUrl = `https://api.mapbox.com/v4/mapbox.terrain-rgb/${tile.z}/${tile.x}/${tile.y}@2x.pngraw?access_token=${accessToken}`;

  try {
    // Fetch and cache the tile blob
    const blob = await fetchAndCacheBlob(tileUrl, cache);

    // Calculate the pixel coordinates within the tile
    const pixelCoords = getPixelCoordinates(coord, tile, 512); // Assuming tile size is 512

    // Ensure pixel coordinates are within bounds
    if (
      pixelCoords.x < 0 ||
      pixelCoords.y < 0 ||
      pixelCoords.x >= 512 ||
      pixelCoords.y >= 512
    ) {
      throw new Error('Pixel coordinates are out of bounds');
    }

    // Get elevation from the blob
    const elevation = await getElevationFromBlob(
      blob,
      pixelCoords.x,
      pixelCoords.y
    );

    let measurementPreference = MeasurementPrefUtility.preference()
    return '• ' + measurementPreference.smallUnitFormatter(elevation);
  } catch (error) {
    throw new Error(`Failed to get elevation: ${error.message}`);
  }
}



